import { useEffect, useRef, useState } from 'react';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { usePutMeterDisplayNameMutation } from '../../../api/energy-analysis/portfolioAnalysisApiSlice';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

/**
 * MeterDisplayName
 * Props:
 * - recalcAccountId (string|number)
 * - meterId (string|number)
 * - analysisType (string)
 * - displayName (string) initial value
 */
const MeterDisplayName = ({
  recalcAccountId,
  meterId,
  analysisType,
  displayName: initialDisplayName,
  address,
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initialDisplayName || '');
  const inputRef = useRef(null);

  const [putMeterDisplayName, { isLoading }] = usePutMeterDisplayNameMutation();

  useEffect(() => {
    setValue(initialDisplayName || '');
  }, [initialDisplayName]);

  useEffect(() => {
    if (editing) {
      // focus and select the text when entering edit mode
      inputRef.current?.focus();
      inputRef.current?.select?.();
    }
  }, [editing]);

  const submit = async () => {
    // avoid unnecessary call
    if (!recalcAccountId || !meterId || value === initialDisplayName) {
      setEditing(false);
      return;
    }

    try {
      await putMeterDisplayName({
        recalc_account_id: recalcAccountId,
        meter_id: meterId,
        analysisType,
        displayName: value,
      }).unwrap();

      // keep the updated value in local state and exit edit mode
      setEditing(false);
    } catch (err) {
      // on error keep editing so user can retry; log for debugging
      // eslint-disable-next-line no-console
      console.error('Failed to update meter display name', err);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      submit();
    } else if (e.key === 'Escape') {
      // cancel
      setValue(initialDisplayName || '');
      setEditing(false);
    }
  };

  return (
    <Box width="100%" sx={{ display: 'flex', alignItems: 'center' }}>
      {!editing ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setEditing(true)}
        >
          <Typography
            variant="h5"
            data-testid="meter-display-name-title"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {value || address}
          </Typography>
          <EditRoundedIcon sx={{ ml: 1 }} fontSize="medium" />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <TextField
            sx={{
              minWidth: 120,
              width: `${Math.max((value || '').length + 5, 10)}ch`,
            }}
            inputRef={inputRef}
            size="small"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={isLoading}
            data-testid="meter-display-name-input"
          />
          <IconButton
            aria-label="save"
            onClick={submit}
            disabled={isLoading}
            size="small"
            sx={{ ml: 1 }}
            data-testid="meter-display-name-save"
          >
            <CheckIcon fontSize="small" />
          </IconButton>
          <IconButton
            aria-label="cancel"
            onClick={() => {
              setValue(initialDisplayName || '');
              setEditing(false);
            }}
            disabled={isLoading}
            size="small"
            sx={{ ml: 0.5 }}
            data-testid="meter-display-name-cancel"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default MeterDisplayName;
